import { type Money, parseItemcode } from '@ifixit/helpers';
import { useTranslations } from '@ifixit/i18n/hooks';
import { type LocaleCode } from '@ifixit/i18n/types';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { MerchProduct } from '../types';

type MerchProductApiResponse = {
   id: string;
   title: string;
   handle: string;
   variants: MerchProductApiVariant[];
};

type MerchProductApiVariant = {
   apparelSize: string;
   proPricesByTier: Record<string, Money>;
   lineItem: CartLineItem;
};

const ALL_MERCH = [
   {
      handle: 'orbit-t-shirt',
      productcode: '179105',
      imageSrc: 'https://assets.cdn.ifixit.com/static/images/cart/product/cart-orbit-tshirt.png',
      i18nHeader: 'addAnOrbitTShirt',
      i18nSubtext: 'orbitSubtext',
   },
   {
      handle: 'exploded-switch-t-shirt',
      productcode: '179107',
      imageSrc:
         'https://assets.cdn.ifixit.com/static/images/cart/product/cart-switch-tshirt-back.png',
      i18nHeader: 'addAnExplodedSwitchTShirt',
      i18nSubtext: 'explodedSwitchSubtext',
   },
   /*
   {
      handle: 'standard-issue-t-shirt',
      productcode: '179030',
      imageSrc:
         'https://assets.cdn.ifixit.com/static/images/cart/product/cart-standard-issue-t-shirt.png',
      i18nHeader: 'addStandardIssueTShirt',
      i18nSubtext: 'standardIssueSubtext',
   },
   */
] as const;

export function useMerchProduct({
   localeCode,
}: {
   localeCode: LocaleCode;
}): MerchProduct | null {
   const apiClient = useIFixitApiClient();
   const t = useTranslations('components.MerchBanner');
   const memoizedMerch = useMemoizedMerch();

   const { data: merchProduct } = useQuery({
      refetchOnMount: false,
      enabled: memoizedMerch != null,
      retry: false,
      queryKey: ['find-merch-product', localeCode, memoizedMerch?.handle ?? 'no-merch-handle'],
      queryFn: async (): Promise<MerchProduct | null> => {
         if (memoizedMerch == null) return null;
         const apiResponse = await apiClient.get<MerchProductApiResponse>(
            `store/merch-product/${localeCode}/${memoizedMerch.handle}`,
            { statName: 'merch-product' }
         );
         return createMerchProduct(apiResponse);
      },
      staleTime: Infinity,
   });
   return merchProduct ?? null;

   function createMerchProduct(product: MerchProductApiResponse): MerchProduct | null {
      if (memoizedMerch == null || product.variants.length === 0) {
         return null;
      }
      return {
         ...product,
         imageSrc: memoizedMerch.imageSrc,
         header: t(memoizedMerch.i18nHeader),
         subtext: t(memoizedMerch.i18nSubtext),
      };
   }
}

function useMemoizedMerch() {
   const { data: cart } = useCart();
   const cartProdutcodes = cart
      ? cart.lineItems.map(line => parseItemcode(line.itemcode).productcode)
      : [];
   const merchNotInCart = ALL_MERCH.filter(merch => !cartProdutcodes.includes(merch.productcode));
   merchNotInCart.sort();
   return useMemo(
      () =>
         merchNotInCart.length > 0
            ? merchNotInCart[Math.floor(Math.random() * merchNotInCart.length)]
            : null,
      // eslint-disable-next-line react-hooks/use-memo
      [JSON.stringify(merchNotInCart)]
   );
}
